<template>
  <ul>
    <ProductConfigurationItem :editable="false">
      <div class="text-base">
        <NuxtLink
          v-if="productPath"
          data-testid="goToPDP"
          :to="productPath"
          :title="product.name"
          class="font-medium tracking-normal"
          >{{ product.name }}</NuxtLink
        >
        <span v-else class="font-medium tracking-normal">
          {{ product.name }}
        </span>
        <span v-if="description" class="block text-neutral-50">{{
          description
        }}</span>
      </div>
    </ProductConfigurationItem>
    <ProductConfigurationItem
      v-for="(item, key) in productConfiguration"
      :key="key"
      :title="item.name"
      :price="item.price"
      :value="formatItemValue(item.price?.withTax || item.value)"
      :locale-options="localeOptions"
      :editable="editable && item.editable"
      @click:edit="$emit('click:edit', item)"
    />
    <ProductConfigurationItem
      :price="totalPrice"
      is-sum
      :editable="false"
      :locale-options="localeOptions"
    >
      <div v-if="showQuantity" class="flex gap-2">
        <FimButton
          v-if="editable"
          type="tertiary"
          class="size-6 border-none text-gray-600"
          no-padding
          data-testid="remove-basket-item"
          @click="$emit('click:remove')"
        >
          <template #icon>
            <LazyIconFielmannTrash class="size-4" />
          </template>
        </FimButton>
        <div
          :class="{
            'pointer-events-none': isQuantityChangeDisabled,
          }"
        >
          {{ $t('basket_card.quantity_label') }}:
          <QuantityDropdown
            v-if="editable"
            no-spacing
            text-medium
            :disabled="isQuantityChangeDisabled"
            class="pl-1"
            :available-quantity="availableQuantity"
            :quantity="quantity"
            @click:change-quantity="
              (quantity: number) => $emit('click:change-quantity', quantity)
            "
          />
          <span v-else class="pl-1">{{ quantity }}</span>
        </div>
      </div>
    </ProductConfigurationItem>
  </ul>
</template>

<script setup lang="ts">
import QuantityDropdown from '../dropdown/QuantityDropdown.vue'
import ProductConfigurationItem from './ProductConfigurationItem.vue'
import type {
  ProductConfigurationItem as IProductConfigurationItem,
  ProductConfigurationMainItem,
  ProductConfigurationRxMainItem,
} from './ProductConfigurationItem'

defineEmits(['click:edit', 'click:remove', 'click:change-quantity'])

const props = defineProps({
  product: {
    type: Object as PropType<
      ProductConfigurationMainItem | ProductConfigurationRxMainItem
    >,
    required: true,
  },
  productConfiguration: {
    type: Array as PropType<IProductConfigurationItem[]>,
    required: true,
  },
  description: {
    type: String as PropType<string>,
    default: '',
  },
  quantity: {
    type: Number as PropType<number>,
    default: 1,
  },
  editable: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  isQuantityChangeDisabled: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  showQuantity: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  availableQuantity: {
    type: Number as PropType<number>,
    default: undefined,
  },
  productPath: {
    type: String as PropType<string>,
    default: '',
  },
  localeOptions: {
    type: Object as PropType<ObjectWithLocaleAndCurrency>,
    default: undefined,
  },
})

const $i18n = useI18n()

const formatItemValue = (value: number | string | undefined) => {
  if (!value) {
    return $i18n.t('pdp.glasses.inclusive')
  }
  if (typeof value === 'number') {
    return toCurrency(value, props.localeOptions)
  }
  return value
}

const totalPrice = computed(() => {
  const prices = props.productConfiguration
    .map((item) => item.price)
    .filter(Boolean)
  return prices.length ? sumUpPrice(prices) : props.product.price
})
</script>
